import { type RefObject, type ReactNode, useEffect, useRef, useState } from 'react';
import {
  Archive,
  CheckCircle2,
  MoreHorizontal,
  Ticket as TicketIcon,
  Trash2,
} from 'lucide-react';
import { formatBytes } from '../../api';
import type { ContactAttachment, ContactConversation, ContactMessage } from '../../app/legacy-types';
import { ConversationAvatar } from './ChannelIdentity';
import type { ContactCenterOperator } from './contactCenterApi';
import {
  attachmentUrl,
  channelLabel,
  contactAttachments,
  deliveryLabel,
  formatContactDateTime,
  groupContactMessages,
  isAudioAttachment,
  isEmojiOnly,
  isImageAttachment,
  isVideoAttachment,
} from './contactCenterModel';
import { EmailThread } from './EmailThread';
import { MessageActions, failedMessageAction, type ContactActivity } from './MessageActions';
import { SafeMessageBody } from './SafeContactMedia';

export type ConversationThreadProps = {
  conversation: ContactConversation;
  messages: ContactMessage[];
  activity: ContactActivity[];
  operators: ContactCenterOperator[];
  targetMessageId?: string | null;
  streamRef: RefObject<HTMLDivElement | null>;
  loading: boolean;
  error: boolean;
  pending: boolean;
  composer: ReactNode;
  imageIndexes: Map<string, number>;
  onPreview: (index: number) => void;
  onRetry: () => void;
  onAssign: (userId: string) => void;
  onStatus: (status: ContactConversation['status']) => void;
  onOpenTicket: () => void;
  onDelete: () => void;
  onEditMessage: (messageId: string, body: string) => Promise<unknown>;
  onDeleteMessage: (messageId: string) => Promise<unknown>;
  onReactMessage: (messageId: string, reaction: string) => Promise<unknown>;
  onRetryAction: (actionId: string) => Promise<unknown>;
};

function attachmentIcon(item: ContactAttachment): string {
  if (item.isVoice || item.type === 'voice') return '🎤';
  if (isAudioAttachment(item)) return '🎧';
  if (isImageAttachment(item)) return '🖼️';
  if (isVideoAttachment(item)) return '🎬';
  return '📎';
}

function ContactAttachmentView({ message, item, index, onPreview }: { message: ContactMessage; item: ContactAttachment; index: number; onPreview?: () => void }) {
  const sourceIndex = item.sourceIndex ?? index;
  const previewUrl = attachmentUrl(message.id, sourceIndex);
  const downloadUrl = attachmentUrl(message.id, sourceIndex, true);
  const name = item.name || 'Файл';
  const mimeType = String(item.mimeType || '').toLowerCase();
  const previewable = isImageAttachment(item) || isVideoAttachment(item) || isAudioAttachment(item)
    || mimeType === 'application/pdf' || mimeType.startsWith('text/') || /\.(pdf|txt|log|json|xml|csv|md)$/i.test(name);
  return <div className={`contact-attachment ${item.type || 'file'} ${item.isVoice ? 'voice' : ''}`}>
    {isImageAttachment(item) && <button type="button" className="contact-attachment-preview" onClick={onPreview} aria-label={`Просмотреть ${name}`}><img src={previewUrl} alt={name} loading="lazy" /></button>}
    {isVideoAttachment(item) && <video src={previewUrl} controls preload="metadata" loop={item.type === 'animation'} autoPlay={item.type === 'animation'} muted={item.type === 'animation'} playsInline />}
    {isAudioAttachment(item) && <div className="contact-audio"><span>{item.isVoice ? '🎤' : '🎧'}</span><audio src={previewUrl} controls preload="metadata" /></div>}
    {!isImageAttachment(item) && !isVideoAttachment(item) && !isAudioAttachment(item) && <span className="contact-file-icon">{attachmentIcon(item)}</span>}
    <div className="contact-attachment-info"><b className="contact-attachment-name" title={name}>{name}</b><small>{item.mimeType || item.type || 'файл'}{item.size ? ` · ${formatBytes(item.size)}` : ''}</small></div>
    <div className="contact-attachment-actions">{previewable && (isImageAttachment(item) ? <button type="button" onClick={onPreview}>Открыть</button> : <a href={previewUrl} target="_blank" rel="noreferrer">Открыть</a>)}<a href={downloadUrl}>Скачать</a></div>
  </div>;
}

export function ConversationThread(props: ConversationThreadProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const rows = groupContactMessages(props.messages);
  const conversation = props.conversation;

  useEffect(() => setMoreOpen(false), [conversation.id]);

  useEffect(() => {
    if (!moreOpen) return;
    const closeOnOutside = (event: MouseEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) setMoreOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [moreOpen]);

  return <div className="contact-conversation-column">
    <header className="contact-chat-head v2">
      <div className="contact-center-v3-chat-identity"><ConversationAvatar provider={conversation.provider} name={conversation.customer_name || conversation.title || 'Собеседник'} /><span><b>{conversation.title}</b><small>{channelLabel(conversation.provider)} · {conversation.channel_name || 'Основной канал'}</small></span></div>
      <div className="contact-chat-head-actions">
        <select aria-label="Ответственный" value={conversation.assigned_user_id || ''} onChange={event => props.onAssign(event.target.value)} disabled={props.pending}><option value="">Без ответственного</option>{props.operators.map(operator => <option key={operator.id} value={operator.id}>{operator.full_name}{operator.is_online ? ' · в сети' : ''}</option>)}</select>
        <select aria-label="Статус обращения" value={conversation.status} onChange={event => props.onStatus(event.target.value as ContactConversation['status'])} disabled={props.pending}><option value="OPEN">Открыт</option><option value="WAITING">Ожидание</option><option value="RESOLVED">Решён</option><option value="CLOSED">Закрыт / архив</option></select>
        {conversation.linked_ticket_id && <button type="button" className="button ghost compact" onClick={props.onOpenTicket}><TicketIcon /> {conversation.linked_ticket_display || 'Открыть заявку'}</button>}
        <div className="contact-chat-more" ref={moreRef}>
          <button type="button" className="icon-button contact-chat-more__trigger" aria-label="Другие действия с диалогом" aria-expanded={moreOpen} onClick={() => setMoreOpen(value => !value)} disabled={props.pending}><MoreHorizontal /></button>
          {moreOpen && <div className="contact-chat-more__menu" role="menu">
            {conversation.status !== 'CLOSED'
              ? <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); props.onStatus('CLOSED'); }}><Archive /> <span><b>Закрыть чат</b><small>Переместить диалог в архив</small></span></button>
              : <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); props.onStatus('OPEN'); }}><CheckCircle2 /> <span><b>Вернуть в работу</b><small>Снова показать среди активных</small></span></button>}
            <button type="button" role="menuitem" className="danger" onClick={() => { setMoreOpen(false); props.onDelete(); }}><Trash2 /> <span><b>Удалить диалог</b><small>Удалить чат и всю переписку</small></span></button>
          </div>}
        </div>
      </div>
    </header>
    <div className="contact-dialog-main">
      <div className="contact-message-stream v2" ref={props.streamRef} aria-live="polite">
        {props.loading && <div className="page-skeleton"><span /><span /><span /></div>}
        {props.error && <div className="form-error">Не удалось загрузить сообщения. <button type="button" onClick={props.onRetry}>Повторить</button></div>}
        {rows.map(row => row.kind === 'date' ? <div key={row.key} className="contact-date-divider"><span>{row.label}</span></div> : conversation.provider === 'EMAIL' && row.item.direction !== 'SYSTEM'
          ? <div key={row.key} data-message-id={row.item.id} className={`contact-email-row ${row.item.direction.toLowerCase()} ${props.targetMessageId === row.item.id ? 'search-target' : ''}`}><EmailThread message={row.item} /><small className={`message-meta ${row.item.provider_status === 'FAILED' ? 'partial' : ''}`}>{deliveryLabel(row.item)}</small><MessageActions message={row.item} failedAction={failedMessageAction(props.activity, row.item.id)} onEdit={props.onEditMessage} onDelete={props.onDeleteMessage} onReact={props.onReactMessage} onRetry={props.onRetryAction} /></div>
          : <div key={row.key} data-message-id={row.item.id} className={`contact-message ${row.item.direction.toLowerCase()} ${row.grouped ? 'grouped' : ''} ${isEmojiOnly(row.item.body) ? 'emoji-only' : ''} ${props.targetMessageId === row.item.id ? 'search-target' : ''}`}>
            {row.item.direction === 'SYSTEM' ? <div className="contact-system-event">{row.item.body}</div> : <>
              {row.showHeader && <span><b>{row.item.sender_name || (row.item.direction === 'OUTBOUND' ? 'Оператор' : 'Клиент')}</b><time>{formatContactDateTime(row.item.created_at)}</time></span>}
              {row.item.deleted_for_everyone_at ? <p className="contact-message-deleted">Сообщение удалено у всех</p> : row.item.body && <p><SafeMessageBody message={row.item} /></p>}
              {!row.item.deleted_for_everyone_at && contactAttachments(row.item).length > 0 && <div className="contact-attachments">{contactAttachments(row.item).map((attachment, index) => {
                const sourceIndex = attachment.sourceIndex ?? index;
                const galleryIndex = props.imageIndexes.get(`${row.item.id}:${sourceIndex}`);
                return <ContactAttachmentView key={`${attachment.storageFileId || attachment.fileId || attachment.url || attachment.name}-${index}`} message={row.item} item={attachment} index={index} onPreview={galleryIndex === undefined ? undefined : () => props.onPreview(galleryIndex)} />;
              })}</div>}
              <small className={`message-meta ${row.item.provider_status === 'FAILED' ? 'partial' : ''}`}>{deliveryLabel(row.item)}</small>
              <MessageActions message={row.item} failedAction={failedMessageAction(props.activity, row.item.id)} onEdit={props.onEditMessage} onDelete={props.onDeleteMessage} onReact={props.onReactMessage} onRetry={props.onRetryAction} />
            </>}
          </div>)}
      </div>
      {props.composer}
    </div>
  </div>;
}
